// components/DeviceConfigTypeSelector.jsx
import React, { useEffect, useMemo } from 'react';
import { Segmented, Tooltip } from 'antd';
import { MobileOutlined, DesktopOutlined, QrcodeOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';

// Export meta so parent can read label/color (for modal title, etc.)
export const DEVICE_TYPE_META = {
    mobile:      { label: 'Entrance control', color: '#52c41a', icon: <MobileOutlined /> },
    desktop:     { label: 'Selfcheckin',   color: '#0078D6', icon: <DesktopOutlined /> },
    leadcapture: { label: 'Lead Capture',  color: '#722ED1', icon: <QrcodeOutlined /> },
};

const DeviceConfigTypeSelector = ({
                                      value,
                                      onChange,
                                      size = 'large',
                                      itemSizePx = 44,
                                      iconSizePx = 20,
                                  }) => {
    const { hasFeature, isAdmin } = useAuth?.() || { hasFeature: () => false, isAdmin: false };

    // Compute allowed types from features
    const allowedTypes = useMemo(() => {
        const t = [];
        if (hasFeature('deviceConfig.mobile')) t.push('mobile');
        if (hasFeature('deviceConfig.desktop')) t.push('desktop');
        if (hasFeature('deviceConfig.leadcapture')) t.push('leadcapture');
        if (t.length === 0 && isAdmin) return ['mobile', 'desktop', 'leadcapture'];
        return t.length ? t : ['mobile'];
    }, [hasFeature, isAdmin]);

    // Normalize persisted value if not allowed
    useEffect(() => {
        if (!allowedTypes.includes(value) && allowedTypes.length) onChange?.(allowedTypes[0]);
    }, [allowedTypes, value, onChange]);

    const options = allowedTypes.map((t) => {
        const m = DEVICE_TYPE_META[t];
        return {
            // Icon-only label; text is used in tooltip
            label: (
                <Tooltip title={m.label} color={m.color}>
                    <div
                        aria-label={m.label} // a11y
                        role="img"
                        style={{
                            // square, center content
                            width: itemSizePx,
                            height: itemSizePx,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            fontSize: iconSizePx,
                        }}
                    >
                        <span style={{ display: 'inline-flex' }}>{m.icon}</span>
                    </div>
                </Tooltip>
            ),
            value: t,
        };
    });

    return (
        <>
            <style>{`
        /* - colorize selected pill by current value via data-attr */
        .device-type-selector .ant-segmented-item { padding: 0 !important; } /* no extra padding around our square */
        .device-type-selector .ant-segmented-item-selected { color: #fff !important; }
        .device-type-selector[data-selected="mobile"]      .ant-segmented-item-selected { background: ${DEVICE_TYPE_META.mobile.color}; }
        .device-type-selector[data-selected="desktop"]     .ant-segmented-item-selected { background: ${DEVICE_TYPE_META.desktop.color}; }
        .device-type-selector[data-selected="leadcapture"] .ant-segmented-item-selected { background: ${DEVICE_TYPE_META.leadcapture.color}; }
        /* - remove default min-width so our square size rules */
        .device-type-selector .ant-segmented-item-label { min-width: 0; }
      `}</style>
            <div className="device-type-selector" data-selected={value}>
                <Segmented options={options} value={value} onChange={onChange} size={size} />
            </div>
        </>
    );
};

export default DeviceConfigTypeSelector;